import React, { useState, useEffect } from 'react';
import { Box, Stack, TextField, Button, IconButton, Checkbox, Typography, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

/**
 * OrderLinesEditor
 * props:
 *  - lines: [{ id?, note, requestedQty, obtainedQty, done, extra? }]
 *  - onChange(lines)
 *  - readonly
 */
const OrderLinesEditor = ({ lines = [], onChange, readonly = false }) => {
  const [draftNote, setDraftNote] = useState('');
  const [draftQty, setDraftQty] = useState('');
  const [localLines, setLocalLines] = useState(lines);

  useEffect(() => {
    const normalized = (lines || []).map(l => {
      const rqRaw = l.requestedQty ?? l.request_qty ?? l.requested_qty ?? l.quantity ?? l.qty;
      const obtRaw = l.obtainedQty ?? l.obtained_qty ?? l.obtained_quantity ?? l.obtained ?? l.received ?? l.done_qty;
      const rq = rqRaw === undefined || rqRaw === null ? 0 : Number(rqRaw);
      const obt = obtRaw === undefined || obtRaw === null ? 0 : Number(obtRaw);
      return {
        ...l,
        requestedQty: rq,
        obtainedQty: obt,
        done: typeof l.done === 'boolean' ? l.done : (obt >= rq && rq > 0)
      };
    });
    // Debug: first line snapshot
    if (normalized.length) {
      // eslint-disable-next-line no-console
      console.log('[OrderLinesEditor] normalized first line', normalized[0]);
    }
    setLocalLines(normalized);
  }, [lines]);

  const emit = (next) => {
    setLocalLines(next);
    onChange && onChange(next);
  };

  const addLine = () => {
    const qty = Number(draftQty) || 0;
    if (!draftNote.trim() || qty <= 0) return;
    const newLine = { id: Math.random().toString(36).slice(2), note: draftNote.trim(), requestedQty: qty, obtainedQty: 0, done: false };
    emit([...localLines, newLine]);
    setDraftNote('');
    setDraftQty('');
  };

  const toggleDone = (idx) => {
    const next = localLines.map((l,i) => i===idx ? { ...l, done: !l.done, obtainedQty: !l.done ? Math.max(l.obtainedQty, l.requestedQty) : l.obtainedQty } : l);
    emit(next);
  };

  const changeObtained = (idx, val) => {
    const num = Math.max(0, Number(val) || 0);
    const next = localLines.map((l,i) => i===idx ? { ...l, obtainedQty: num, done: num >= l.requestedQty ? true : l.done && num >= l.requestedQty } : l);
    emit(next);
  };

  const changeRequested = (idx, val) => {
    const num = Math.max(1, Number(val) || 1);
    const next = localLines.map((l,i) => i===idx ? { ...l, requestedQty: num } : l);
    emit(next);
  };

  const removeLine = (idx) => {
    const next = localLines.filter((_,i) => i!==idx);
    emit(next);
  };

  return (
    <Box display='flex' flexDirection='column' gap={1}>
      {!readonly && (
        <Stack direction='row' spacing={1} alignItems='center'>
          <TextField label='Variante / Nota' size='small' value={draftNote} onChange={e=>setDraftNote(e.target.value)} sx={{ flex: 1 }} />
          <TextField label='Cant.' size='small' value={draftQty} onChange={e=>setDraftQty(e.target.value.replace(/[^0-9]/g,''))} sx={{ width: 90 }} />
          <Button startIcon={<AddIcon />} variant='contained' size='small' onClick={addLine}>Agregar</Button>
        </Stack>
      )}
      {localLines.length === 0 && <Typography variant='caption' color='text.secondary'>Sin líneas</Typography>}
      <Stack spacing={0.5}>
        {localLines.map((l, idx) => {
          const extra = l.obtainedQty > l.requestedQty;
          return (
            <Box key={l.id || idx} display='flex' alignItems='center' gap={1} px={1} py={0.5} border='1px solid' borderColor={l.done ? 'success.light' : 'divider'} borderRadius={1} bgcolor={l.done ? 'success.light' : extra ? 'warning.light' : 'background.paper'}>
              <Checkbox size='small' checked={l.done} onChange={()=>!readonly && toggleDone(idx)} disabled={readonly} />
              <TextField label='Nota' size='small' value={l.note} onChange={e=>!readonly && emit(localLines.map((ln,i)=> i===idx? {...ln, note: e.target.value}: ln))} sx={{ flex: 1 }} disabled={readonly} />
              <TextField type='number' inputProps={{ inputMode:'numeric', min:0 }} label='Ped' size='small' value={l.requestedQty} onChange={e=>!readonly && changeRequested(idx, e.target.value)} sx={{ width:72 }} disabled={readonly} />
              <TextField type='number' inputProps={{ inputMode:'numeric', min:0 }} label='Obt' size='small' value={l.obtainedQty} onChange={e=>!readonly && changeObtained(idx, e.target.value)} sx={{ width:72 }} disabled={readonly} />
              {extra && <Tooltip title='Cantidad mayor a la solicitada'><Typography variant='caption' color='warning.dark'>+{l.obtainedQty - l.requestedQty}</Typography></Tooltip>}
              {!readonly && <IconButton size='small' onClick={()=>removeLine(idx)}><DeleteIcon fontSize='inherit' /></IconButton>}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default OrderLinesEditor;
